import { z } from "zod";
import { fcfa, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis, Refus } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

const RatioSchema = z.object({
  code: z.string(),
  libelle: z.string(),
  valeur: z.number().nullable(),
  unite: z.string(),
  seuil: z.number().nullable(),
  respecte: z.boolean().nullable(),
  lecture: z.string().nullable(),
});

const Schema = z.object({
  etapes: z.array(
    z.object({
      titre: z.string(),
      ratios: z.array(RatioSchema),
      lecture: z.string(),
    }),
  ),
  altman: z.object({ z: z.number(), zone: z.string(), lecture: z.string() }),
  valeurEconomiqueAjoutee: z.number(),
  coutMoyenPondereDuCapital: z.number(),
  actifEconomique: z.number(),
  besoinEnFondsDeRoulement: z.number(),
  noteConformiteFinanciere: z.string(),
});

function formater(r: z.infer<typeof RatioSchema>) {
  if (r.valeur === null) return "non calculable";
  if (r.unite === "%") return `${(r.valeur * 100).toFixed(1)} %`;
  if (r.unite === "F") return fcfa(r.valeur);
  if (r.unite === "x") return `${r.valeur.toFixed(2)} x`;
  if (r.unite === "jours") return `${Math.round(r.valeur)} j`;
  return r.valeur.toFixed(2);
}

export function Diagnostic({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["diagnostic", dossierId, annee],
    `/dossiers/${dossierId}/etats/diagnostic?annee=${annee}`,
    Schema,
  );

  if (q.isLoading) return <EtatChargement quoi="du diagnostic financier" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  // ⛔ RÈGLE D'OR : aucun chiffre n'est communiqué et aucune note n'est
  // prononcée tant que les trois contrôles bloquants ne passent pas.
  if (!q.data?.valeur) return <Refus avis={q.data?.avis ?? []} quoi="Le diagnostic financier" />;

  const d = q.data.valeur;

  return (
    <div>
      <p className="resume">
        Démarche Vernimmen en cinq temps, sur une base dont les trois contrôles
        bloquants sont passés. <em>Un ratio calculé sur un bilan déséquilibré est
        un mensonge présenté avec deux décimales.</em>
      </p>

      <Tuiles
        tuiles={[
          { titre: "Note de conformité financière", valeur: d.noteConformiteFinanciere },
          {
            titre: "Score Altman Z''",
            valeur: d.altman.z.toFixed(2),
            note: `zone ${d.altman.zone}`,
            alerte: d.altman.zone === "de detresse",
          },
          {
            titre: "Valeur économique ajoutée",
            valeur: fcfa(d.valeurEconomiqueAjoutee),
            alerte: d.valeurEconomiqueAjoutee < 0,
          },
          { titre: "Actif économique", valeur: fcfa(d.actifEconomique) },
          { titre: "Besoin en fonds de roulement", valeur: fcfa(d.besoinEnFondsDeRoulement) },
          {
            titre: "Coût moyen pondéré du capital",
            valeur: `${(d.coutMoyenPondereDuCapital * 100).toFixed(1)} %`,
          },
        ]}
      />

      {d.etapes.map((e) => (
        <section key={e.titre}>
          <h2>{e.titre}</h2>
          <Tableau
            colonnes={[
              { cle: "lib", titre: "Ratio", rendu: (r) => r.libelle },
              { cle: "val", titre: "Valeur", montant: true, rendu: formater },
              {
                cle: "seuil",
                titre: "Seuil",
                montant: true,
                rendu: (r) =>
                  r.seuil === null
                    ? "—"
                    : r.unite === "%"
                      ? `${(r.seuil * 100).toFixed(0)} %`
                      : r.unite === "jours"
                        ? `${r.seuil} j`
                        : `${r.seuil} x`,
              },
              {
                cle: "resp",
                titre: "Respecté",
                rendu: (r) =>
                  r.respecte === null ? (
                    "—"
                  ) : (
                    <span className={r.respecte ? "oui" : "non"}>
                      {r.respecte ? "oui" : "non"}
                    </span>
                  ),
              },
            ]}
            lignes={e.ratios}
            cle={(r) => r.code}
          />
          <p className="lecture">{e.lecture}</p>
        </section>
      ))}

      <section>
        <h2>Score de défaillance</h2>
        <p className="lecture">{d.altman.lecture}</p>
      </section>

      <ListeAvis avis={q.data.avis} titre="Paramètres et observations" />
    </div>
  );
}
